import {useDispatch, useSelector} from "react-redux";
import {useState} from "react";
import useLazyListLoader from "../../util/lasy-loader/useLazyListLoader.js";
import Table from "../../util/table/Table.jsx";
import Counter from "../../util/Counter.jsx";
import './AssetHistory.scss'
import {
    getHistoryArray,
    getIsCalendarPercent,
    getIsStatsCardVisible,
    setIsCalendarPercent,
    setIsStatsCardVisible
} from "../../../ducks/history-duck.js";
import {
    getAssetIds,
    getCurrentAsset,
    getFilter,
    getFilterPreset,
    getIsSingleAsset,
    setDateFilter
} from "../../../ducks/assets-duck.js";
import {DECIMAL_FORMAT} from "../../../util/numbers.js";
import DirectionIcon from "../../util/icons/DirectionIcon.jsx";
import HorizontalLineIcon from "mdi-react/HorizontalLineIcon.js";
import TableIcon from "mdi-react/TableIcon.js";
import CalendarMonthIcon from "mdi-react/CalendarMonthIcon.js";
import {getDirectionColorClassName, isMobile} from "../../../util/misc.js";
import ChangeFlavorIcon from "../../util/icons/ChangeFlavorIcon.jsx";
import LoaderHorizontal from "../../util/loaders/LoaderHorizontal.jsx";
import DateDiffHighlighter from "../../util/DateDiffHighlighter.jsx";
import dayjs from "dayjs";
import {isToday} from "../../../util/time-and-date.js";
import {scrollToTop, useNavigation} from "../../util/navigation/navigation.js";
import AddRateButton from "./AddRateButton.jsx";
import {getIsOddy} from "../../../ducks/user-duck.js";
import {getStorage, setStorage} from "../../../api/_token.js";
import AssetHistoryCalendar from "./AssetHistoryCalendar.jsx";
import HistoryStatsCard from "./HistoryStatsCard.jsx";
import FilterToggleButtons from "../../util/filter-toggle-buttons/FilterToggleButtons";
import FilterToggleButton, {FILTER_TOGGLE_BUTTON_STATE} from "../../util/filter-toggle-buttons/FilterToggleButton";
import PercentIcon from "mdi-react/PercentIcon";
import ScaleBalanceIcon from "mdi-react/ScaleBalanceIcon";

const VIEWS = {
    TABLE: 'table',
    CALENDAR: 'calendar',
}

export default () => {

    const dispatch = useDispatch()
    const history = useSelector(getHistoryArray)
    const filter = useSelector(getFilter)
    const filterPreset = useSelector(getFilterPreset)
    const {is_usd_asset} = useSelector(getCurrentAsset)
    const isOddy = useSelector(getIsOddy)
    const isSingleAsset = useSelector(getIsSingleAsset)
    const isPercent = useSelector(getIsCalendarPercent)
    const isStatsCardVisible = useSelector(getIsStatsCardVisible)

    const [view, setView] = useState(() => getStorage('history-view', VIEWS.CALENDAR))
    const {navigateToDashBoard} = useNavigation(scrollToTop)

    const {visibleList, ListLoaderComponent} = useLazyListLoader([...history].reverse())

    const applyHistoryDateFilter = date => {
        if (isToday(date)) {
            dispatch(setDateFilter(null))
        } else {
            dispatch(setDateFilter(date))
        }
        navigateToDashBoard(filterPreset)
    }

    const selectView = view => {
        setStorage('history-view', view)
        setView(view)
    }

    if (history.length < 10) {
        return (
            <div className='asset-history loader-container'>
                <LoaderHorizontal/>
            </div>
        )
    }

    return (
        <div className='asset-history'>
            <div className='toolbar flex-vertical-center'>
                <FilterToggleButtons>
                    <FilterToggleButton
                        icon={<CalendarMonthIcon/>}
                        state={view === VIEWS.CALENDAR ? FILTER_TOGGLE_BUTTON_STATE.ACTIVE : FILTER_TOGGLE_BUTTON_STATE.PASSIVE}
                        onClick={() => selectView(VIEWS.CALENDAR)}
                    >
                        Calendar
                    </FilterToggleButton>
                    <FilterToggleButton
                        icon={<TableIcon/>}
                        state={view === VIEWS.TABLE ? FILTER_TOGGLE_BUTTON_STATE.ACTIVE : FILTER_TOGGLE_BUTTON_STATE.PASSIVE}
                        onClick={() => selectView(VIEWS.TABLE)}
                    >
                        Table
                    </FilterToggleButton>
                </FilterToggleButtons>
                <FilterToggleButtons>
                    {view === VIEWS.CALENDAR && isMobile() &&
                        <FilterToggleButton
                            icon={<PercentIcon/>}
                            state={isPercent ? FILTER_TOGGLE_BUTTON_STATE.ACTIVE : FILTER_TOGGLE_BUTTON_STATE.PASSIVE}
                            onClick={() => dispatch(setIsCalendarPercent(!isPercent))}
                        />
                    }
                    <FilterToggleButton
                        icon={<ScaleBalanceIcon/>}
                        state={isStatsCardVisible ? FILTER_TOGGLE_BUTTON_STATE.ACTIVE : FILTER_TOGGLE_BUTTON_STATE.PASSIVE}
                        onClick={() => dispatch(setIsStatsCardVisible(!isStatsCardVisible))}
                    />
                </FilterToggleButtons>
            </div>
            <HistoryStatsCard/>
            {view === VIEWS.CALENDAR ?
                <AssetHistoryCalendar isPercent={isPercent}/>
                :
                <>
                    <Table>
                        <thead>
                        <tr>
                            <th className='hide-portrait'/>
                            <th>
                                Date
                            </th>
                            <th className={`numeric ${isOddy && isSingleAsset ? 'shift-left' : ''}`}>
                                Rate
                            </th>
                            <th className='numeric hide-portrait'>
                                Value
                            </th>
                            <th className='numeric hide-portrait'>
                                Return
                            </th>
                            <th className='numeric'>
                                Change
                            </th>
                            <th className='hide-portrait'/>
                        </tr>
                        </thead>
                        <tbody>
                        {visibleList.map(date =>
                            <HistoryDate
                                key={`${date[0]}|${history.length}|${filter}`}
                                date={date[0]}
                                data={date[1]}
                                isUsdAsset={is_usd_asset}
                                applyHistoryDateFilter={applyHistoryDateFilter}
                            />)
                        }
                        </tbody>
                    </Table>
                    <ListLoaderComponent/>
                </>
            }
        </div>
    )
}

const HistoryDate = ({
                         date, data, isUsdAsset, applyHistoryDateFilter = () => {
    }
                     }) => {

    const assetIds = useSelector(getAssetIds)

    const {
        value,
        valueYesterday,
        gain,
        gainChange,
        rate,
        usdRate,
    } = assetIds.reduce((acc, cur) => ({
        value: acc.value + data[cur].value,
        valueYesterday: acc.valueYesterday + data[cur].valueYesterday,
        gain: acc.gain + data[cur].gain,
        gainChange: acc.gainChange + (data[cur].value - (data[cur].value / (1 + data[cur].gainChangePercent))),
        rate: acc.rate + data[cur].rate,
        usdRate: acc.usdRate + data[cur].usdRate,
    }), {
        value: 0,
        valueYesterday: 0,
        gain: 0,
        gainChange: 0,
        rate: 0,
        usdRate: 0,
    })

    const gainChangePercent = gainChange / valueYesterday
    const changeClassName = getDirectionColorClassName(gainChangePercent, true)
    const dateHasChange = gainChange !== 0

    return (
        <tr onClick={() => applyHistoryDateFilter(date)}>
            <td className='fade-left hide-portrait'/>
            <td className='date'>
                <div className='flex-vertical-center'>
                    <DateDiffHighlighter date={date}/> <ChangeFlavorIcon amount={gainChangePercent}/>
                </div>
                <div className='small weak'>
                    {dayjs(date).format('ddd')}
                </div>
            </td>
            <td className={`numeric rate`}>
                {assetIds.length > 1 ?
                    <HorizontalLineIcon className='no-value'/>
                    :
                    <div className='flex-vertical-center justify-end'>
                        <Counter
                            amount={isUsdAsset ? usdRate : rate}
                            decimals={DECIMAL_FORMAT.FLEX_DEC}
                            prefix={isUsdAsset ? '$ ' : 'kr '}
                        />
                        <AddRateButton
                            date={date}
                            rate={rate}
                            dateHasRate={dateHasChange}
                        />
                    </div>
                }

            </td>
            <td className={`numeric hide-portrait`}>
                <Counter
                    amount={value}
                />
            </td>
            <td className={`numeric hide-portrait`}>
                <Counter
                    className={getDirectionColorClassName(gain)}
                    amount={gain}
                    absolute={true}
                    decimals={DECIMAL_FORMAT.FLEX_DEC}
                />
            </td>
            <td className={`numeric `}>
                {!dateHasChange ?
                    <>
                        <HorizontalLineIcon className='no-value'/>
                    </>
                    :
                    <>
                        <DirectionIcon value={gainChangePercent} isMultiColored={true}/>
                        <Counter
                            amount={gainChange}
                            absolute={true}
                        />
                        <div className={`small ${changeClassName}`}>
                            <Counter
                                amount={gainChangePercent * 100}
                                decimals={DECIMAL_FORMAT.FLEX_PERCENTAGE}
                                suffix='%'
                                absolute={true}
                            />
                        </div>
                    </>
                }
            </td>
            <td className='fade-right hide-portrait'/>
        </tr>
    )
}